import { EnumSelect } from "@/components/ui/EnumSelect";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import { Download } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import type {
  SpaceDescriptor,
  SpaceCategory,
  ConditionsPresetKey,
  SpaceMedium,
  SpaceConditions,
  SpaceSoundtrack,
  SpaceTerrain,
  SpaceVisibility,
} from "@mind-sands/shared";
import {
  SPACE_TERRAINS,
  SPACE_SKIES,
  TIMES_OF_DAY,
  SPACE_MEDIUMS,
} from "@mind-sands/shared";
import { spaceThemeOrder, spaceThemes } from "@/lib/spaceThemes";
import { api } from "../api";
import { useSpaceEditor } from "../hooks/useSpaceEditor";
import { useSpacePermissions } from "../hooks/useSpacePermissions";
import { useSceneStore } from "../stores/sceneStore";
import { downloadSpaceJson } from "../lib/downloadSpaceJson";
import { ConditionsEditor } from "./ConditionsEditor";
import { SoundtrackEditor } from "./SoundtrackEditor";

const MAX_SPACE_DESCRIPTION_LENGTH = 500;

function LabeledRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <label className="text-[10px] font-medium text-sands-500 uppercase tracking-wider w-16 shrink-0">
        {label}
      </label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function GalleryVisibilityRow() {
  const { canEdit } = useSpacePermissions();
  const spaceId = useSceneStore((s) => s.activeSpaceId);
  const visibility = useSceneStore((s) => s.spaceVisibility);
  const setSpaceVisibility = useSceneStore((s) => s.setSpaceVisibility);
  const [saving, setSaving] = useState(false);

  if (!canEdit || !spaceId) return null;

  const handleChange = async (next: SpaceVisibility) => {
    setSaving(true);
    try {
      const result = await api.updateSpaceVisibility(spaceId, next);
      setSpaceVisibility(result.visibility);
    } finally {
      setSaving(false);
    }
  };

  return (
    <LabeledRow label="Gallery">
      <Select
        value={visibility}
        disabled={saving}
        onChange={(e) => {
          void handleChange(e.target.value as SpaceVisibility);
        }}
      >
        <option value="public">Shown on landing</option>
        <option value="private">Hidden from landing</option>
      </Select>
    </LabeledRow>
  );
}

function ExportJsonButton() {
  const { canEdit } = useSpacePermissions();
  const space = useSceneStore((s) => s.activeSpaceDescriptor);

  if (!canEdit || !space) return null;

  return (
    <button
      type="button"
      onClick={() => downloadSpaceJson(space)}
      className="flex w-full items-center justify-center gap-1.5 rounded-md border border-sands-200 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-sands-600 hover:bg-sands-50 hover:text-sands-800"
    >
      <Download className="h-3.5 w-3.5" />
      Download JSON
    </button>
  );
}

function SpacePanelFooter() {
  const { canEdit } = useSpacePermissions();
  if (!canEdit) return null;

  return (
    <div className="shrink-0 border-t border-sands-100 px-3 py-2">
      <GalleryVisibilityRow />
      <ExportJsonButton />
    </div>
  );
}

function SpaceListingSection({
  activeSpace,
  onCategoryChange,
  onDescriptionChange,
}: {
  activeSpace: SpaceDescriptor;
  onCategoryChange: (category: SpaceCategory) => void;
  onDescriptionChange: (description: string) => void;
}) {
  return (
    <section className="mb-4 pb-3 border-b border-sands-100">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-sands-500">
        Landing gallery
      </div>

      <LabeledRow label="Mood">
        <Select
          value={activeSpace.category}
          onChange={(e) => {
            onCategoryChange(e.target.value as SpaceCategory);
          }}
        >
          {spaceThemeOrder.map((category) => (
            <option key={category} value={category}>
              {spaceThemes[category].emoji} {spaceThemes[category].label}
            </option>
          ))}
        </Select>
      </LabeledRow>

      <div className="flex items-start gap-2 mb-2">
        <label
          htmlFor="space-description"
          className="text-[10px] font-medium text-sands-500 uppercase tracking-wider w-16 shrink-0 pt-2"
        >
          Blurb
        </label>
        <div className="flex-1 min-w-0">
          <Textarea
            id="space-description"
            value={activeSpace.description}
            maxLength={MAX_SPACE_DESCRIPTION_LENGTH}
            rows={3}
            inputSize="sm"
            className="min-h-[4.5rem] text-sm leading-relaxed"
            placeholder="Short description shown on the landing card"
            onChange={(e) => {
              onDescriptionChange(e.target.value);
            }}
          />
          <p className="mt-1 text-[10px] text-sands-400 text-right tabular-nums">
            {activeSpace.description.length}/{MAX_SPACE_DESCRIPTION_LENGTH}
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * Semantic space editor panel.
 * Edits SpaceDescriptor fields (listing, terrain, sky, conditions, medium, timeOfDay, soundtracks).
 */
export function SpacePanel() {
  const { activeSpace, updateField, updateListingField } = useSpaceEditor();

  const handleTerrainChange = useCallback(
    (value: SpaceDescriptor["terrain"]) => {
      void updateField({ terrain: value });
    },
    [updateField],
  );

  const handleSkyChange = useCallback(
    (value: SpaceDescriptor["sky"]) => {
      void updateField({ sky: value });
    },
    [updateField],
  );

  const handleConditionsChange = useCallback(
    (value: ConditionsPresetKey) => {
      void updateField({ conditions: value });
    },
    [updateField],
  );

  const handleConditionsFieldChange = useCallback(
    (updated: SpaceConditions) => {
      void updateField({ conditions: updated });
    },
    [updateField],
  );

  const handleMediumChange = useCallback(
    (value: SpaceMedium) => {
      void updateField({ medium: value });
    },
    [updateField],
  );

  const handleTimeOfDayChange = useCallback(
    (value: SpaceDescriptor["timeOfDay"]) => {
      void updateField({ timeOfDay: value });
    },
    [updateField],
  );

  const handleSoundtracksChange = useCallback(
    (soundtracks: SpaceSoundtrack[] | undefined) => {
      void updateField({ soundtracks });
    },
    [updateField],
  );

  const handleCategoryChange = useCallback(
    (category: SpaceCategory) => {
      updateListingField({ category });
    },
    [updateListingField],
  );

  const handleDescriptionChange = useCallback(
    (description: string) => {
      updateListingField({ description });
    },
    [updateListingField],
  );

  if (!activeSpace) {
    return (
      <div className="p-4 text-sm text-sands-400 text-center">
        No space selected
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <SpaceListingSection
          activeSpace={activeSpace}
          onCategoryChange={handleCategoryChange}
          onDescriptionChange={handleDescriptionChange}
        />

        <div
          className="mb-1"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            columnGap: "0.75rem",
          }}
        >
          <LabeledRow label="Terrain">
            <Select
              value={activeSpace.terrain ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                handleTerrainChange(v === "" ? null : (v as SpaceTerrain));
              }}
            >
              <option value="">None (floating)</option>
              {SPACE_TERRAINS.map((t) => (
                <option key={t} value={t}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </option>
              ))}
            </Select>
          </LabeledRow>

          <LabeledRow label="Sky">
            <EnumSelect
              value={activeSpace.sky}
              options={SPACE_SKIES}
              onChange={handleSkyChange}
            />
          </LabeledRow>

          <LabeledRow label="Medium">
            <EnumSelect
              value={activeSpace.medium ?? "air"}
              options={SPACE_MEDIUMS}
              onChange={handleMediumChange}
            />
          </LabeledRow>

          <LabeledRow label="Time">
            <EnumSelect
              value={activeSpace.timeOfDay}
              options={TIMES_OF_DAY}
              onChange={handleTimeOfDayChange}
            />
          </LabeledRow>
        </div>

        <div className="mb-2">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-sands-500">
            Conditions
          </div>
          <ConditionsEditor
            conditions={activeSpace.conditions}
            onPresetChange={handleConditionsChange}
            onConditionsChange={handleConditionsFieldChange}
          />
        </div>

        <SoundtrackEditor
          soundtracks={activeSpace.soundtracks}
          onChange={handleSoundtracksChange}
        />
      </div>

      <SpacePanelFooter />
    </div>
  );
}
